import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import 'twin.macro';
import { getRealtimeAudioReport } from '../../services/realtime/debug';
import {
  clearRealtimeTimeline,
  getRealtimeTimeline,
  subscribeRealtimeTimeline,
} from '../../services/realtime/event-log';
import { useRealtimeConnectionStore } from '../../services/realtime-connection-store';

const STORAGE_KEY = 'realtime-debug';
const POSITION_KEY = 'realtime-debug-position';
const COLLAPSED_KEY = 'realtime-debug-collapsed';
const POLL_INTERVAL_MS = 700;

// Every corner of the exercise page already has something in it (counters,
// MediaPipe preview, hold telemetry), so the panel cycles rather than fighting
// for one spot.
const POSITIONS = ['bottom-left', 'bottom-right', 'top-left', 'top-right'];

const POSITION_STYLE = {
  'bottom-left': { bottom: '0.5rem', left: '0.5rem' },
  'bottom-right': { bottom: '0.5rem', right: '0.5rem' },
  'top-left': { top: '0.5rem', left: '0.5rem' },
  'top-right': { top: '0.5rem', right: '0.5rem' },
};

const readStored = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch (_error) {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (_error) {
    // Storage can be unavailable; the in-memory toggle still works.
  }
};

/**
 * Visible by default, but only ever mounted in development (see App.jsx) —
 * a real patient must never see this readout. `'0'` is stored on close, so
 * hiding it sticks across reloads within a dev session.
 */
const readInitialVisibility = () => {
  try {
    if (new URLSearchParams(window.location.search).get('realtimeDebug') === '1') {
      return true;
    }
  } catch (_error) {
    /* no location in a non-browser context */
  }

  const stored = readStored(STORAGE_KEY, null);
  if (stored === '1') {
    return true;
  }
  if (stored === '0') {
    return false;
  }

  return true;
};

const formatNumber = value => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value !== 'number') {
    return String(value);
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(4);
};

/**
 * Reduces the whole chain to one sentence, because the useful question is never
 * "what are all the numbers" but "which link is broken".
 */
function describeVerdict(report, textSource) {
  if (!report) {
    return { text: 'no report yet', tone: 'idle' };
  }

  const { connection, inboundAudio, audioElement, monitor } = report;

  // Checked ahead of the connection/transport branches below: a text-only
  // response (no audio_transcript delta ever arrived for it) is not a
  // playback failure — OpenAI never generated audio for this particular
  // reply, so every check further down would otherwise mislabel it.
  if (textSource === 'text' && (!inboundAudio || inboundAudio.bytesReceived === 0)) {
    return { text: 'TEXT-ONLY REPLY — OpenAI generated no audio for this one', tone: 'warn' };
  }

  if (connection.gateBlockedBy?.length) {
    return {
      text: `GATE BLOCKED — waiting on ${connection.gateBlockedBy.join(', ')}`,
      tone: 'bad',
    };
  }
  if (connection.status === 'idle' || connection.status === null) {
    return { text: 'NOT STARTED — connect() was never called', tone: 'bad' };
  }
  if (connection.status === 'error') {
    return { text: `CONNECTION ERROR — ${connection.error ?? 'unknown'}`, tone: 'bad' };
  }
  if (connection.status === 'connecting') {
    return { text: 'CONNECTING — token / SDP / register in flight', tone: 'idle' };
  }
  if (!inboundAudio || inboundAudio.bytesReceived === 0) {
    return { text: 'NO AUDIO BYTES FROM OPENAI — backend side', tone: 'bad' };
  }
  if (!audioElement?.hasSrcObject) {
    return { text: 'BYTES ARRIVING but <audio> has no stream', tone: 'bad' };
  }
  if (audioElement.paused) {
    return { text: 'BYTES ARRIVING but playback is paused/blocked', tone: 'bad' };
  }
  if (audioElement.muted || audioElement.volume === 0) {
    return { text: 'BYTES ARRIVING but the element is muted', tone: 'bad' };
  }
  if ((inboundAudio.totalAudioEnergy ?? 0) === 0) {
    return { text: 'BYTES ARRIVING but they are digital silence', tone: 'warn' };
  }

  return {
    text: `AUDIO PLAYING — rms ${formatNumber(monitor.lastRms)}`,
    tone: 'good',
  };
}

const TONE_COLOR = {
  good: '#4ade80',
  warn: '#fbbf24',
  bad: '#f87171',
  idle: '#94a3b8',
};

// One colour per origin, so the feed can be read at a glance: who did what.
const SOURCE_COLOR = {
  openai: '#60a5fa',
  webrtc: '#c084fc',
  audio: '#4ade80',
  api: '#fbbf24',
  error: '#f87171',
  system: '#94a3b8',
};

const formatClock = timestamp => new Date(timestamp).toTimeString().slice(0, 8);

const Row = ({ label, value }) => (
  <div tw="flex justify-between gap-3">
    <span tw="text-[#94a3b8]">{label}</span>
    <span tw="text-right break-all">{String(value)}</span>
  </div>
);

/**
 * On-screen twin of `window.__realtimeAudioReport()`. Dev-only (App.jsx gates
 * mounting on `import.meta.env.MODE === 'development'`).
 *
 * The console version is useless on a phone and easy to miss on a desktop that
 * never reached the connect call at all, which is exactly the case that needs
 * diagnosing. Toggle with Ctrl+Alt+R, `?realtimeDebug=1`, or
 * `localStorage.setItem('realtime-debug','1')`.
 */
const RealtimeDebugPanel = () => {
  const [visible, setVisible] = useState(readInitialVisibility);
  // Collapsed is not closed: the panel shrinks to a single handle so the exercise
  // underneath can be watched, and one tap brings the whole readout back. Closing
  // (✕) still needs the keyboard shortcut to undo, which is why both exist.
  const [collapsed, setCollapsed] = useState(() => readStored(COLLAPSED_KEY, '0') === '1');
  const [position, setPosition] = useState(() => readStored(POSITION_KEY, POSITIONS[0]));
  const [report, setReport] = useState(null);
  const [actionNote, setActionNote] = useState('');

  const connectionStatus = useRealtimeConnectionStore(state => state.connectionStatus);
  const sessionId = useRealtimeConnectionStore(state => state.sessionId);
  const exerciseId = useRealtimeConnectionStore(state => state.exerciseId);
  const runtime = useRealtimeConnectionStore(state => state.runtime);
  const latestAssistantText = useRealtimeConnectionStore(state => state.latestAssistantText);
  const latestAssistantTextSource = useRealtimeConnectionStore(
    state => state.latestAssistantTextSource
  );

  // Outside zustand on purpose — entries arrive faster than a reactive store
  // should re-render the page for. See services/realtime/event-log.js.
  const timeline = useSyncExternalStore(subscribeRealtimeTimeline, getRealtimeTimeline);

  useEffect(() => {
    const handleKeyDown = event => {
      // `event.code`, not `event.key`: on a non-Latin layout (Arabic here) the
      // key of the physical R is 'ق', so a `key === 'r'` check never fires.
      if (event.ctrlKey && event.altKey && event.code === 'KeyR') {
        event.preventDefault();
        setVisible(previous => {
          const next = !previous;
          writeStored(STORAGE_KEY, next ? '1' : '0');
          // Keeps the console logging in step with the panel.
          window.__realtimeDebugEnabled = next;
          return next;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    // Collapsed counts as not shown: getStats() every 700ms is not free, and
    // nothing is on screen to read the result.
    if (!visible || collapsed) {
      return undefined;
    }

    let cancelled = false;
    const tick = async () => {
      const next = await getRealtimeAudioReport();
      if (!cancelled) {
        setReport(next);
      }
    };

    tick();
    const timer = window.setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [visible, collapsed]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(previous => {
      const next = !previous;
      writeStored(COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const cyclePosition = useCallback(() => {
    setPosition(previous => {
      const next = POSITIONS[(POSITIONS.indexOf(previous) + 1) % POSITIONS.length];
      writeStored(POSITION_KEY, next);
      return next;
    });
  }, []);

  // A play() call from inside a click is the one thing that beats the autoplay
  // policy, so this button both diagnoses and fixes that case.
  const forcePlay = useCallback(async () => {
    const element = runtime?.audioElement;
    if (!element) {
      setActionNote('no <audio> element');
      return;
    }

    element.muted = false;
    element.volume = 1;

    try {
      await element.play();
      setActionNote('play() resolved');
    } catch (error) {
      setActionNote(`play() rejected: ${error?.name ?? error}`);
    }
  }, [runtime]);

  // Proves the speakers/output device work, independent of the whole WebRTC
  // path — if this is silent too, nothing about the agent is the problem.
  const playTestTone = useCallback(async () => {
    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContextCtor();
      await context.resume().catch(() => {});

      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 440;
      gain.gain.value = 0.1;
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.6);
      oscillator.onended = () => context.close().catch(() => {});
      setActionNote('test tone played');
    } catch (error) {
      setActionNote(`tone failed: ${error?.name ?? error}`);
    }
  }, []);

  if (!visible) {
    return null;
  }

  // Collapsed: one handle, same corner, same z-index. Nothing else — the point is
  // to get the panel out of the way without losing the way back to it.
  if (collapsed) {
    return (
      <button
        type="button"
        tw="fixed z-[99999] grid h-12 w-12 place-items-center rounded-[14px] bg-black/85 text-[22px] leading-none text-[#e2e8f0]"
        style={{ backdropFilter: 'blur(2px)', ...POSITION_STYLE[position] }}
        title="expand the realtime audio panel"
        aria-label="expand the realtime audio panel"
        onClick={toggleCollapsed}
      >
        ‹
      </button>
    );
  }

  const verdict = describeVerdict(report, latestAssistantTextSource);
  const inbound = report?.inboundAudio ?? null;
  const element = report?.audioElement ?? null;

  // z-99999 clears every overlay the exercise page draws — the MediaPipe
  // preview sits at z-900 and the hold telemetry at z-9000.
  return (
    <div
      tw="fixed z-[99999] max-h-[85vh] w-[380px] max-w-[calc(100vw-1rem)] overflow-auto rounded-lg bg-black/85 p-3 font-mono text-[11px] leading-4 text-white"
      style={{ backdropFilter: 'blur(2px)', ...POSITION_STYLE[position] }}
    >
      <div tw="mb-2 flex items-center justify-between">
        <strong>realtime audio</strong>
        <div tw="flex gap-2">
          <button
            type="button"
            tw="text-[#94a3b8]"
            title="move to next corner"
            onClick={cyclePosition}
          >
            ⤢
          </button>
          <button
            type="button"
            tw="text-[#94a3b8]"
            title="collapse to a handle"
            aria-label="collapse the realtime audio panel"
            onClick={toggleCollapsed}
          >
            ⌄
          </button>
          <button
            type="button"
            tw="text-[#94a3b8]"
            onClick={() => {
              writeStored(STORAGE_KEY, '0');
              setVisible(false);
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <div tw="mb-2 rounded p-1.5" style={{ color: TONE_COLOR[verdict.tone] }}>
        {verdict.text}
      </div>

      <Row label="status" value={connectionStatus} />
      <Row label="sessionId" value={sessionId ?? '— (gate not passed)'} />
      <Row label="exerciseId" value={exerciseId ?? '—'} />
      <Row label="callId" value={report?.connection.callId ?? '—'} />
      <Row label="pc state" value={report?.connection.connectionState ?? '—'} />
      <Row label="ice" value={report?.connection.iceConnectionState ?? '—'} />
      <Row label="ice gathering" value={report?.connection.iceGatheringState ?? '—'} />
      <Row label="error" value={report?.connection.error ?? '—'} />
      <Row label="gate" value={report?.connection.gateBlockedBy?.join(', ') || 'open'} />
      <Row label="autoplay" value={report?.connection.playbackBlocked ?? 'ok'} />
      <Row
        label="reconnect"
        value={
          report?.connection.reconnecting
            ? `retrying (attempt ${report.connection.reconnectAttempt})`
            : 'no'
        }
      />

      <div tw="my-2 border-t border-white/20 pt-2">inbound rtp</div>
      <Row label="packets" value={formatNumber(inbound?.packetsReceived)} />
      <Row label="bytes" value={formatNumber(inbound?.bytesReceived)} />
      <Row label="lost" value={formatNumber(inbound?.packetsLost)} />
      <Row label="energy" value={formatNumber(inbound?.totalAudioEnergy)} />
      <Row label="level" value={formatNumber(inbound?.audioLevel)} />
      <Row label="concealed" value={formatNumber(inbound?.concealedSamples)} />

      <div tw="my-2 border-t border-white/20 pt-2">audio element</div>
      <Row label="srcObject" value={element ? String(element.hasSrcObject) : '—'} />
      <Row label="paused" value={element ? String(element.paused) : '—'} />
      <Row label="muted" value={element ? String(element.muted) : '—'} />
      <Row label="volume" value={element ? formatNumber(element.volume) : '—'} />
      <Row label="rms" value={formatNumber(report?.monitor.lastRms)} />

      <div tw="my-2 border-t border-white/20 pt-2">diagnostics</div>
      <Row
        label="tone"
        value={
          report?.monitor.toneAnomalyCount
            ? `${report.monitor.lastTonePeakHz}Hz @ ${report.monitor.lastToneProminenceDb}dB (x${report.monitor.toneAnomalyCount})`
            : 'none detected'
        }
      />
      {/* Elevated tick lag means the main thread is starved — a buzz reported
          alongside it points at the pose pipeline, not the audio path. */}
      <Row label="tick lag" value={formatNumber(report?.monitor.tickLagMs)} />
      <Row
        label="openai error"
        value={
          report?.openAiErrors.last
            ? `${report.openAiErrors.last} (x${report.openAiErrors.counts?.[report.openAiErrors.last] ?? 1})`
            : 'none'
        }
      />

      <div tw="my-2 border-t border-white/20 pt-2">what the agent said</div>
      <Row
        label="source"
        value={
          latestAssistantTextSource === 'text'
            ? 'text (no audio was generated)'
            : latestAssistantTextSource === 'audio_transcript'
              ? 'audio_transcript'
              : '—'
        }
      />
      <div tw="max-h-24 overflow-auto rounded bg-white/5 p-1.5">
        {latestAssistantText || <span tw="text-[#94a3b8]">nothing spoken yet</span>}
      </div>

      <div tw="my-2 flex items-center justify-between border-t border-white/20 pt-2">
        <span>feed ({timeline.length})</span>
        <button type="button" tw="text-[#94a3b8]" onClick={clearRealtimeTimeline}>
          clear
        </button>
      </div>
      <div tw="max-h-64 overflow-auto">
        {timeline.length === 0 && <div tw="text-[#94a3b8]">nothing has happened yet</div>}
        {timeline
          .slice()
          .reverse()
          .map(entry => (
            <button
              key={entry.id}
              type="button"
              tw="flex w-full gap-1.5 py-0.5 text-left"
              // Clicking prints the untouched event — the summary is a
              // convenience, the payload is the evidence.
              onClick={() => console.warn(`[realtime] ${entry.type}`, entry.detail ?? entry)}
            >
              <span tw="shrink-0 text-[#64748b]">{formatClock(entry.at)}</span>
              <span tw="shrink-0" style={{ color: SOURCE_COLOR[entry.source] ?? '#e2e8f0' }}>
                {entry.type}
              </span>
              <span tw="break-all text-[#cbd5e1]">{entry.summary}</span>
            </button>
          ))}
      </div>

      <div tw="mt-3 flex gap-2">
        <button type="button" tw="rounded bg-white/15 px-2 py-1" onClick={forcePlay}>
          force play
        </button>
        <button type="button" tw="rounded bg-white/15 px-2 py-1" onClick={playTestTone}>
          test tone
        </button>
      </div>
      {actionNote && <div tw="mt-1 text-[#fbbf24]">{actionNote}</div>}
    </div>
  );
};

export default RealtimeDebugPanel;
